import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Toaster } from "./components/ui/toaster";

// Customer Portal Pages (lazy-loaded so each route ships as its own small chunk)
const Home = lazy(() => import("./pages/Home"));
const Services = lazy(() => import("./pages/Services"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const Booking = lazy(() => import("./pages/Booking"));
const Quote = lazy(() => import("./pages/Quote"));
const Track = lazy(() => import("./pages/Track"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SEORepairPage = lazy(() => import("./pages/SEORepairPage"));
const RepairSlugRouter = lazy(() => import("./pages/RepairSlugRouter"));
const RepairsHub = lazy(() => import("./pages/RepairsHub"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex"));
const GuideArticle = lazy(() => import("./pages/GuideArticle"));
const TradeInEstimator = lazy(() => import("./pages/TradeInEstimator"));
const FinancingOptions = lazy(() => import("./pages/FinancingOptions"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const LoyaltyPage = lazy(() => import("./pages/LoyaltyPage"));
const LiveInventory = lazy(() => import("./pages/LiveInventory"));
const ContentGenerator = lazy(() => import("./pages/ContentGenerator"));
const POSKnowledgebaseHub = lazy(() => import("./pages/POSKnowledgebaseHub"));
const ServiceCityLanding = lazy(() => import("./pages/ServiceCityLanding"));
import { SERVICE_SLUGS } from "./data/servicePages";
import SEO_LANDING_PAGES from "./data/seoLanding.json";

// POS Portal Pages
const POSDashboard = lazy(() => import("./pages/POSDashboard"));
const POSCheckIn = lazy(() => import("./pages/POSCheckIn"));
const POSTicketDetail = lazy(() => import("./pages/POSTicketDetail"));
const TicketPrintView = lazy(() => import("./pages/TicketPrintView"));
const SaleReceiptPrintView = lazy(() => import("./pages/SaleReceiptPrintView"));
const EstimatePrintView = lazy(() => import("./pages/EstimatePrintView"));
const LabelTestPrint = lazy(() => import("./pages/LabelTestPrint"));
const LabelPrint = lazy(() => import("./pages/LabelPrint"));
const POSSaleHub = lazy(() => import("./pages/POSSaleHub"));
const POSLayout = lazy(() => import("./pages/POSLayout"));

// POS Repair Pages
const POSManageTickets = lazy(() => import("./pages/POSManageTickets"));
const POSManageLeads = lazy(() => import("./pages/POSManageLeads"));
const POSManageEstimates = lazy(() => import("./pages/POSManageEstimates"));
const POSManageWarranty = lazy(() => import("./pages/POSManageWarranty"));

// POS Inventory Pages
const POSStock = lazy(() => import("./pages/POSStock"));
const POSPurchaseOrders = lazy(() => import("./pages/POSPurchaseOrders"));
const POSReceiveOrders = lazy(() => import("./pages/POSReceiveOrders"));
const POSManageRMA = lazy(() => import("./pages/POSManageRMA"));
const POSTransferInventory = lazy(() => import("./pages/POSTransferInventory"));
const POSStoreTransfers = lazy(() => import("./pages/POSStoreTransfers"));
const POSInventoryCount = lazy(() => import("./pages/POSInventoryCount"));
const POSInventoryAdjustment = lazy(() => import("./pages/POSInventoryAdjustment"));
const POSUnrecognizedScans = lazy(() => import("./pages/POSUnrecognizedScans"));

// POS Other Pages
const POSCustomers = lazy(() => import("./pages/POSCustomers"));
const POSCustomerDetail = lazy(() => import("./pages/POSCustomerDetail"));
const POSReports = lazy(() => import("./pages/POSReports"));
const POSInvoices = lazy(() => import("./pages/POSInvoices"));
const POSReportView = lazy(() => import("./pages/POSReportView"));
const POSMailInRepairs = lazy(() => import("./pages/POSMailInRepairs"));
const AdminKnowledgebase = lazy(() => import("./pages/AdminKnowledgebase"));
const Shop = lazy(() => import("./pages/Shop"));
const ShopProductDetail = lazy(() => import("./pages/ShopProductDetail"));
const ShopOrders = lazy(() => import("./pages/ShopOrders"));
const Contact = lazy(() => import("./pages/Contact"));
const MailInRepair = lazy(() => import("./pages/MailInRepair"));
const POSMarketing = lazy(() => import("./pages/POSMarketing"));
const POSSettings = lazy(() => import("./pages/POSSettings"));
const POSSchedule = lazy(() => import("./pages/POSSchedule"));
const POSContactSupport = lazy(() => import("./pages/POSContactSupport"));
const POSNotifications = lazy(() => import("./pages/POSNotifications"));

// Admin Portal Pages
const AdminLayout = lazy(() => import("./pages/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminRepairs = lazy(() => import("./pages/AdminRepairs"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminLocations = lazy(() => import("./pages/AdminLocations"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminInventory = lazy(() => import("./pages/AdminInventory"));
const AdminMarketplaceOrders = lazy(() => import("./pages/AdminMarketplaceOrders"));
const POSMarketplace = lazy(() => import("./pages/POSMarketplace"));
const AdminSuppliers = lazy(() => import("./pages/AdminSuppliers"));
const AdminTradeIn = lazy(() => import("./pages/AdminTradeIn"));
const AdminSupportChats = lazy(() => import("./pages/AdminSupportChats"));
const AdminTransactions = lazy(() => import("./pages/AdminTransactions"));
const AdminClients = lazy(() => import("./pages/AdminClients"));
const AdminClientDetail = lazy(() => import("./pages/AdminClientDetail"));
const AdminBilling = lazy(() => import("./pages/AdminBilling"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
import ChatWidget from "./components/ChatWidget";
import CdpPreviewBanner from "./components/CdpPreviewBanner";

// Customer Layout Component
function CustomerLayout({ children }) {
  const { siteClient } = useAuth();

  // On a tenant's custom domain: block /admin and /pos routes — they should never
  // be reachable outside the main corporate domain.
  useEffect(() => {
    if (!siteClient) return;
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/pos')) {
      window.location.href = '/';
    }
  }, [siteClient]);

  // Reflect tenant branding in document title + favicon.
  // - Main site: "Total Wireless Repair Center — Phone, Tablet & Computer Repair" + /logo-tw.png
  // - Tenant custom domain: tenant name + tenant logo (falls back to /logo-tw.png).
  useEffect(() => {
    const defaultTitle = 'Total Wireless Repair Center — Phone, Tablet & Computer Repair';
    const defaultIcon = '/logo-tw.png';

    if (siteClient?.name) {
      document.title = siteClient.name;
    } else {
      document.title = defaultTitle;
    }

    const iconHref = siteClient?.branding?.logo_url || defaultIcon;
    ['link[rel="icon"]', 'link[rel="shortcut icon"]', 'link[rel="apple-touch-icon"]']
      .forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute('href', iconHref);
      });
  }, [siteClient]);

  return (
    <>
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <ChatWidget />
      <CdpPreviewBanner />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App min-h-screen flex flex-col bg-[#050505]">
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#050505]"><div className="h-8 w-8 rounded-full border-2 border-[#E3000F] border-t-transparent animate-spin" data-testid="route-loader" /></div>}>
          <Routes>
            {/* CUSTOMER PORTAL (/) */}
            <Route path="/" element={<CustomerLayout><Home /></CustomerLayout>} />
            <Route path="/services" element={<CustomerLayout><Services /></CustomerLayout>} />
            <Route path="/locations" element={<CustomerLayout><Locations /></CustomerLayout>} />
            <Route path="/locations/:state/:city/:address" element={<CustomerLayout><LocationDetail /></CustomerLayout>} />
            <Route path="/locations/:id" element={<CustomerLayout><LocationDetail /></CustomerLayout>} />
            {/* Per-city service landing pages (SEO): /phone-repair/southfield-mi, /iphone-repair/ferndale-mi, etc. */}
            {SERVICE_SLUGS.map((slug) => (
              <Route
                key={`svc-${slug}`}
                path={`/${slug}/:cityState`}
                element={<CustomerLayout><ServiceCityLanding service={slug} /></CustomerLayout>}
              />
            ))}
            <Route path="/booking" element={<CustomerLayout><Booking /></CustomerLayout>} />
            <Route path="/mail-in" element={<CustomerLayout><MailInRepair /></CustomerLayout>} />
            <Route path="/quote" element={<CustomerLayout><Quote /></CustomerLayout>} />
            <Route path="/track" element={<CustomerLayout><Track /></CustomerLayout>} />
            <Route path="/login" element={<CustomerLayout><Login /></CustomerLayout>} />
            <Route path="/reset-password" element={<CustomerLayout><ResetPassword /></CustomerLayout>} />
            <Route path="/register" element={<CustomerLayout><Register /></CustomerLayout>} />
            <Route path="/dashboard" element={<CustomerLayout><Dashboard /></CustomerLayout>} />
            <Route path="/shop" element={<CustomerLayout><Shop /></CustomerLayout>} />
            <Route path="/shop/orders" element={<CustomerLayout><ShopOrders /></CustomerLayout>} />
            <Route path="/shop/product/:id" element={<CustomerLayout><ShopProductDetail /></CustomerLayout>} />
            <Route path="/shop/product/:id/:slug" element={<CustomerLayout><ShopProductDetail /></CustomerLayout>} />
            <Route path="/contact" element={<CustomerLayout><Contact /></CustomerLayout>} />
            <Route path="/trade-in" element={<CustomerLayout><TradeInEstimator /></CustomerLayout>} />
            <Route path="/financing" element={<CustomerLayout><FinancingOptions /></CustomerLayout>} />
            <Route path="/repairs" element={<CustomerLayout><RepairsHub /></CustomerLayout>} />
            <Route path="/guides" element={<CustomerLayout><GuidesIndex /></CustomerLayout>} />
            <Route path="/guides/:slug" element={<CustomerLayout><GuideArticle /></CustomerLayout>} />
            <Route path="/repair/:slug" element={<CustomerLayout><RepairSlugRouter /></CustomerLayout>} />
            {/* Static SEO landing pages (device -> brand -> problem). Discoverable via
                sitemap/search only; intentionally NOT surfaced in customer nav. */}
            {Object.keys(SEO_LANDING_PAGES).map((slug) => (
              <Route key={`seo-${slug}`} path={`/${slug}`} element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            ))}
            <Route path="/buy-used-iphones" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/sell-your-phone" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/phone-accessories" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/same-day-repairs" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />

            {/* Mail-In Repair SEO Pages */}
            <Route path="/mail-in-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/mail-in-iphone-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/mail-in-android-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/mail-in-ipad-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/mail-in-tablet-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />

            {/* Computer / Laptop / Gaming SEO Pages (top-level URLs) */}
            <Route path="/computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/desktop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-screen-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-battery-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-charging-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-keyboard-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/laptop-liquid-damage" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/virus-removal" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/data-recovery" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/gaming-pc-builder" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/gaming-pc-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/apple-mac-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/dell-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/dell-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/hp-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/hp-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/lenovo-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/lenovo-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/asus-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/asus-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/acer-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/acer-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/msi-gaming-laptop-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/msi-gaming-computer-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />

            {/* Tablet / iPad / Samsung Tablet SEO Pages (top-level URLs) */}
            <Route path="/tablet-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/tablet-screen-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-screen-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/tablet-battery-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-battery-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/tablet-charging-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-charging-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-water-damage-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-pro-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-air-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-mini-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-10th-generation-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-9th-generation-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ipad-8th-generation-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/samsung-tablet-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/samsung-galaxy-tab-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/galaxy-tab-screen-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/galaxy-tab-battery-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />

            {/* Game Console / PS5 / Xbox / Nintendo Switch SEO Pages */}
            <Route path="/game-console-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/playstation-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-hdmi-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-disc-drive-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-overheating-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-power-supply-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-liquid-damage-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-fan-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-no-signal-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-wont-turn-on" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/ps5-blue-light-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-series-x-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-series-s-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-hdmi-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-disc-drive-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-overheating-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-power-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-no-signal-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/xbox-wont-turn-on" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-screen-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-charging-port-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-joy-con-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-battery-replacement" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-water-damage-repair" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-not-charging" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/nintendo-switch-black-screen" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
            <Route path="/review/:requestId" element={<CustomerLayout><ReviewPage /></CustomerLayout>} />
            <Route path="/loyalty" element={<CustomerLayout><LoyaltyPage /></CustomerLayout>} />
            <Route path="/inventory" element={<CustomerLayout><LiveInventory /></CustomerLayout>} />
            
            {/* POS PORTAL (/pos) */}
            <Route path="/pos" element={<POSLayout><POSDashboard /></POSLayout>} />
            
            {/* POS Repairs */}
            <Route path="/pos/repairs/tickets" element={<POSLayout><POSManageTickets /></POSLayout>} />
            <Route path="/pos/repairs/leads" element={<POSLayout><POSManageLeads /></POSLayout>} />
            <Route path="/pos/repairs/estimates" element={<POSLayout><POSManageEstimates /></POSLayout>} />
            <Route path="/pos/repairs/warranty" element={<POSLayout><POSManageWarranty /></POSLayout>} />
            <Route path="/pos/repairs/mail-in" element={<POSLayout><POSMailInRepairs /></POSLayout>} />
            <Route path="/pos/check-in" element={<POSLayout><POSCheckIn /></POSLayout>} />
            <Route path="/pos/tickets/:trackingNumber" element={<POSLayout><POSTicketDetail /></POSLayout>} />
            <Route path="/pos/tickets/:tracking/print/:format" element={<TicketPrintView />} />
            <Route path="/pos/sales/:id/print/:format" element={<SaleReceiptPrintView />} />
            <Route path="/pos/estimates/:quoteNumber/print" element={<EstimatePrintView />} />
            <Route path="/pos/label-test-print" element={<LabelTestPrint />} />
            <Route path="/pos/tickets/:tracking/label-print" element={<LabelPrint />} />
            
            {/* POS Inventory */}
            <Route path="/pos/inventory/stock" element={<POSLayout><POSStock /></POSLayout>} />
            <Route path="/pos/products" element={<Navigate to="/pos/inventory/stock" replace />} />
            <Route path="/pos/inventory/purchase-orders" element={<POSLayout><POSPurchaseOrders /></POSLayout>} />
            <Route path="/pos/inventory/receive-orders" element={<POSLayout><POSReceiveOrders /></POSLayout>} />
            <Route path="/pos/inventory/rma" element={<POSLayout><POSManageRMA /></POSLayout>} />
            <Route path="/pos/inventory/transfers" element={<POSLayout><POSTransferInventory /></POSLayout>} />
            <Route path="/pos/inventory/store-transfers" element={<POSLayout><POSStoreTransfers /></POSLayout>} />
            <Route path="/pos/inventory/count" element={<POSLayout><POSInventoryCount /></POSLayout>} />
            <Route path="/pos/inventory/adjustments" element={<POSLayout><POSInventoryAdjustment /></POSLayout>} />
            <Route path="/pos/inventory/unrecognized-scans" element={<POSLayout><POSUnrecognizedScans /></POSLayout>} />
            
            {/* POS Other */}
            <Route path="/pos/customers" element={<POSLayout><POSCustomers /></POSLayout>} />
            <Route path="/pos/customers/:customerId" element={<POSLayout><POSCustomerDetail /></POSLayout>} />
            <Route path="/pos/sale" element={<POSLayout><POSSaleHub /></POSLayout>} />
            <Route path="/pos/invoices" element={<POSLayout><POSInvoices /></POSLayout>} />
            <Route path="/pos/pickup-orders" element={<Navigate to="/pos/sale?tab=pickup" replace />} />
            <Route path="/pos/reports" element={<POSLayout><POSReports /></POSLayout>} />
            <Route path="/pos/reports/:reportId" element={<POSLayout><POSReportView /></POSLayout>} />
            <Route path="/pos/marketing" element={<POSLayout><POSMarketing /></POSLayout>} />
            <Route path="/pos/schedule" element={<POSLayout><POSSchedule /></POSLayout>} />
            <Route path="/pos/content-generator" element={<POSLayout><ContentGenerator /></POSLayout>} />
            <Route path="/pos/ai-assistant" element={<Navigate to="/pos/knowledgebase?tab=ai" replace />} />
            <Route path="/pos/knowledgebase" element={<POSLayout><POSKnowledgebaseHub /></POSLayout>} />
            <Route path="/pos/marketplace" element={<POSLayout><POSMarketplace /></POSLayout>} />
            <Route path="/pos/settings" element={<POSLayout><POSSettings /></POSLayout>} />
            <Route path="/pos/custom-domain" element={<Navigate to="/pos/settings?cat=custom-domain" replace />} />
            <Route path="/pos/site-branding" element={<Navigate to="/pos/settings?cat=site-branding" replace />} />
            <Route path="/pos/support" element={<POSLayout><POSContactSupport /></POSLayout>} />
            <Route path="/pos/notifications" element={<POSLayout><POSNotifications /></POSLayout>} />
            
            {/* ADMIN PORTAL (/admin) */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="repairs" element={<AdminRepairs />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="knowledgebase" element={<AdminKnowledgebase />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="inventory" element={<AdminInventory />} />
              <Route path="catalog" element={<Navigate to="/admin/inventory" replace />} />
              <Route path="marketplace-orders" element={<AdminMarketplaceOrders />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="tradein" element={<AdminTradeIn />} />
              <Route path="support" element={<AdminSupportChats />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="clients/:clientId" element={<AdminClientDetail />} />
              <Route path="billing" element={<AdminBilling />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="transactions" element={<AdminTransactions />} />
            </Route>

            {/* Wildcard catch-all (MUST be last). Handles dynamic /computer-repair-{city}
                local SEO pages and falls back to a 404 inside SEORepairPage. */}
            <Route path="*" element={<CustomerLayout><SEORepairPage /></CustomerLayout>} />
          </Routes>
          </Suspense>
          <Toaster />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
